import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { Network } from '@capacitor/network'
import { App } from '@/app'
import { APIServer } from '@/app/data/api'
import { useAuthStore } from '@/app/data/auth'
import { isApp } from '@/index'
import { SQL } from '@/lib/Sqlite'
import { prepareSQLiteValue } from '@/lib/Sqlite/schema'
import { SQLiteEventBus, SQLiteUpdateEvent } from '@/lib/sqliteEventBus'
import { getRandomString } from '@/lib/Functions'
import SQLite from '../../../lib/Task/sqlite'
import Merge from '../../../lib/Task/merge'
import { SyncManager } from '@/app/sync/syncManager'
import { syncQueue, JobPriority } from '@/app/sync'
import { DependencyNotReadyError } from '@/app/sync/syncError'
import { filterStaleMergedItems } from '@/app/sync/mergeWriteGuard'
import { isRowDeletedLocally, hasInFlightJob } from '@/app/sync/jobGuards'
import { parseRow, getLastSortKey, sortByKeys } from '@/app/util'
import { FieldsUpdatedAt, QualityInspectionResult } from '../types'
import { useQualityInspectionStore } from './qualityInspection'

// 품질점검 결과 셀 스토어 (세션 x 항목 x 세부위치)
// 세션(inspection)이 아직 로컬-only인 경우 DependencyNotReadyError로 업로드를 대기시킨다.
const storeKey = 'quality_inspection_results'

// 생성/수정 시 서버로 보내는 컬럼 목록 (inspection_id는 업로드 시점에 서버 id로 치환)
const UPLOAD_KEYS = [
  'item_id',
  'task_id',
  'location',
  'location_detail',
  'result',
  'note',
  'note_2',
  'note_3',
  'set_count',
  'primary_set',
  'custom_defect_content',
  'floorplan_id',
  'shape_type',
  'shape_data',
  'balloon_number',
  'sort_key',
  'fields_updated_at',
] as const

// 업로드 페이로드 구성 (허용 컬럼만 추출)
function pickUploadData(data: QualityInspectionResult): Partial<QualityInspectionResult> {
  const payload: Record<string, unknown> = {}
  for (const key of UPLOAD_KEYS) {
    payload[key] = data[key]
  }
  return payload as Partial<QualityInspectionResult>
}

// 업로드 페이로드 (빈 값 제거 - 단, 비우기를 허용하는 메모·수정 하자내용은 빈 문자열도 전송)
function buildUploadPayload(data: QualityInspectionResult): Partial<QualityInspectionResult> {
  return {
    ...(Merge.returnHasValue(pickUploadData(data)) as Partial<QualityInspectionResult>),
    note: data.note ?? '',
    note_2: data.note_2 ?? '',
    note_3: data.note_3 ?? '',
    custom_defect_content: data.custom_defect_content ?? '',
  }
}

export const useQualityResultStore = defineStore(storeKey, () => {
  const results = ref<QualityInspectionResult[]>([])
  const currentTaskDatabaseId = ref<string>('')

  // SQLite 이벤트 리스너 참조 (중복 등록 방지 및 cleanup용)
  let currentListener: ((event: SQLiteUpdateEvent) => void) | null = null

  const notUploadedResults = computed(() => {
    return results.value.filter(item => item.modifiedFromLocal)
  })

  async function uploadNotUploaded(): Promise<void> {
    if (notUploadedResults.value.length > 0) {
      await requestUpdate(notUploadedResults.value)
    }
  }

  // SQLite 개별 컬럼 갱신 + 이벤트 발행
  async function individualSetLocalModified(
    localStringId: string,
    keyValues: { key: string; value: unknown }[] = [],
  ): Promise<void> {
    const db = await SQL.getDatabase()
    let sql = `UPDATE ${storeKey} SET `
    for (let i = 0; i < keyValues.length; i++) {
      sql += `${keyValues[i].key} = ?`
      if (i < keyValues.length - 1) sql += ', '
    }
    sql += ' WHERE localStringId = ?'
    // 값 정규화: iOS native bridge가 number를 TEXT 컬럼에 "557.0"으로 저장하는 것을 차단
    await db.run(sql, [...keyValues.map(item => prepareSQLiteValue(item.value)), localStringId])

    await SQLiteEventBus.getInstance().emitUpdate(storeKey, 'update', [localStringId])
  }

  /**
   * SQLite 이벤트 리스너 설정
   * @param taskId 현재 task ID
   */
  function setupSQLiteListener(taskId: string): void {
    if (currentListener) {
      SQLiteEventBus.getInstance().offTableUpdated(storeKey, currentListener)
    }

    currentListener = async (event: SQLiteUpdateEvent): Promise<void> => {
      try {
        if (!SQL.createdTables.has(storeKey)) return
        const db = await SQL.getDatabase()

        if (event.type === 'bulk' || event.ids.length === 0) {
          const localData = await db.query(`SELECT * FROM ${storeKey} WHERE task_id = ?`, [taskId])
          results.value = (localData.values ?? []).map((row: unknown) =>
            parseRow(row),
          ) as QualityInspectionResult[]
        } else {
          for (const id of event.ids) {
            const result = await db.query(
              `SELECT * FROM ${storeKey} WHERE localStringId = ? AND task_id = ?`,
              [id, taskId],
            )
            if (result.values && result.values.length > 0) {
              const updatedItem = parseRow(result.values[0]) as QualityInspectionResult
              const index = results.value.findIndex(item => item.localStringId === id)
              if (index !== -1) {
                results.value[index] = updatedItem
              } else {
                results.value.push(updatedItem)
              }
            }
          }
        }
      } catch (error) {
        console.error('[QualityResultStore] Failed to process SQLite event:', error)
      }
    }

    SQLiteEventBus.getInstance().onTableUpdated(storeKey, currentListener)
  }

  async function getServerData(taskId: string): Promise<QualityInspectionResult[]> {
    if (!taskId) return []
    try {
      const data = await APIServer.getQualityResultListByTaskId(Number(taskId))
      return data
        .filter(item => !item.deleted_at)
        .map(item => ({
          ...item,
          localStringId: item.id.toString(),
          modifiedFromLocal: false,
        }))
    } catch (error) {
      console.error('[QualityResultStore] 서버 데이터 조회 실패:', error)
      throw error
    }
  }

  /**
   * taskId 단위 데이터 로드 (온라인: 서버+로컬 병합, 오프라인: 로컬)
   * @param taskId 과업 ID
   * @param signal 취소 시그널
   * @param skipServerSync 동일 과업 재진입 시 서버 병합 스킵
   */
  async function initData(
    taskId: string,
    signal?: AbortSignal,
    skipServerSync = false,
  ): Promise<void> {
    currentTaskDatabaseId.value = taskId

    if (!isApp()) {
      // 웹: SQLite 없이 서버 데이터만 메모리 적재
      if ((await Network.getStatus()).connected) {
        results.value = await getServerData(taskId)
      } else {
        App.toastInstance.add('error', '네트워크 연결이 필요합니다.')
      }
      return
    }

    setupSQLiteListener(taskId)
    if (skipServerSync) return

    try {
      let mergedData = (await SQLite.getLocalData(
        storeKey,
        'task_id',
        taskId,
        false,
      )) as unknown as QualityInspectionResult[]
      if (signal?.aborted) return

      let didMerge = false
      if ((await Network.getStatus()).connected) {
        const serverData = await getServerData(taskId)
        if (signal?.aborted) return

        await SQLite.checkTableExists(storeKey, serverData)

        // 서버 빈 배열 + 로컬 비삭제 데이터 존재 시 병합 스킵 (일시 장애로 인한 소실 방지)
        const activeLocalData = mergedData.filter(item => !item.deleted_at)
        if (serverData.length > 0 || activeLocalData.length === 0) {
          mergedData = Merge.mergeBothData(mergedData, serverData, storeKey)
          didMerge = true
        }
      }

      if (signal?.aborted) return
      results.value = mergedData

      if (didMerge && mergedData.length > 0 && !SyncManager.isTaskSyncing(taskId)) {
        const { itemsToProcess, columns } = await SQLite.getNeedUpdateItems(mergedData, storeKey)
        // 병합 중 업로드 job이 교체한 row의 옛 스냅샷은 저장 제외 (유령 row 부활 방지)
        const safeItems = await filterStaleMergedItems(
          itemsToProcess as { item: QualityInspectionResult; itemId: string }[],
          storeKey,
          taskId,
        )
        await SQLite.updateSqliteOnly(
          safeItems,
          columns,
          currentTaskDatabaseId.value,
          storeKey,
          true,
        )
      }
    } catch (error) {
      if (signal?.aborted) return
      console.error('QualityResultStore.initData ERROR:', error)
      throw error
    }
  }

  /**
   * taskId 단위 조회 (온라인이면 서버+로컬 병합, 오프라인이면 로컬)
   * @param taskId 과업 ID
   * @returns 비삭제 결과 목록 (sort_key 정렬)
   */
  async function getByTaskId(taskId: string): Promise<QualityInspectionResult[]> {
    await initData(taskId)
    return getResults.value
  }

  // 비삭제 결과 목록 (sort_key 정렬)
  const getResults = computed((): QualityInspectionResult[] => {
    return sortByKeys(results.value.filter(item => !item.deleted_at))
  })

  // 특정 세션의 결과 목록 (세션의 localStringId·서버 id 모두 매칭)
  const getResultsByInspection = computed(() => {
    return (
      inspectionDatabaseId: string,
      serverId?: number | string,
    ): QualityInspectionResult[] => {
      const keys = new Set<string>([`${inspectionDatabaseId}`])
      if (serverId !== undefined && serverId !== null) keys.add(`${serverId}`)
      return sortByKeys(
        results.value.filter(item => !item.deleted_at && keys.has(`${item.inspection_id}`)),
      )
    }
  })

  // 단건 조회 (localStringId 또는 서버 id)
  const GetItem = computed(() => {
    return (resultDatabaseId: string): QualityInspectionResult | undefined => {
      return results.value.find(
        item =>
          `${item.localStringId}` === `${resultDatabaseId}` ||
          `${item.id}` === `${resultDatabaseId}`,
      )
    }
  })

  const requestUpdate = async (datas: QualityInspectionResult[]): Promise<void> => {
    if (!isApp()) {
      // 웹: SQLite/syncQueue 우회, APIServer 직접 호출
      await webRequestUpdate(datas)
      return
    }
    const { itemsToProcess, columns } = await SQLite.getNeedUpdateItems(datas, storeKey)
    await SQLite.updateSqliteOnly(itemsToProcess, columns, currentTaskDatabaseId.value, storeKey)
    await addApiQueue(itemsToProcess as { item: QualityInspectionResult; itemId: string }[])
  }

  // 웹 전용: 메모리 항목에 서버 응답을 반영한다
  function applyWebPatch(localStringId: string, patch: Partial<QualityInspectionResult>): void {
    const index = results.value.findIndex(item => item.localStringId === localStringId)
    if (index !== -1) {
      results.value[index] = { ...results.value[index], ...patch }
    }
  }

  // 웹 전용: 세션의 서버 id 확정값 조회 (웹은 세션 Add가 즉시 서버 id를 받으므로 항상 숫자여야 함)
  function findInspectionServerIdWeb(inspectionKey: string): number {
    const inspection = useQualityInspectionStore().GetItem(String(inspectionKey))
    if (inspection) {
      const serverId = Number(inspection.id)
      if (String(inspection.id).includes('_need_to_upload') || isNaN(serverId)) {
        throw new Error(`점검(${inspectionKey})이 아직 서버에 저장되지 않았습니다.`)
      }
      return serverId
    }

    // 스토어 미스 폴백: dev HMR로 스토어 모듈이 재로드되면 점검 목록이 비어
    // 조회에 실패할 수 있음 - 키 자체가 서버 id(숫자)면 그대로 사용 (서버가 소유권 재검증)
    const numericKey = Number(inspectionKey)
    if (
      String(inspectionKey).trim() !== '' &&
      !String(inspectionKey).includes('_need_to_upload') &&
      !isNaN(numericKey)
    ) {
      return numericKey
    }

    throw new Error(`점검(${inspectionKey})이 아직 서버에 저장되지 않았습니다.`)
  }

  /**
   * 웹 전용 저장 경로: syncQueue 없이 APIServer를 직접 호출하고 응답으로 메모리를 갱신한다
   * @param datas 저장할 결과 목록 (Add/UpdateValue/Delete가 메모리 반영 후 전달)
   */
  async function webRequestUpdate(datas: QualityInspectionResult[]): Promise<void> {
    for (const data of datas) {
      const isNew = String(data.id).includes('_need_to_upload')
      const isDeleted = !!data.deleted_at
      try {
        if (isDeleted) {
          // 서버에 생성된 적 없는 항목은 서버 호출 없이 종료
          if (!isNew) {
            await APIServer.deleteQualityResults([{ id: Number(data.id) }])
          }
          applyWebPatch(data.localStringId, { modifiedFromLocal: false })
        } else if (isNew) {
          const payload = {
            ...buildUploadPayload(data),
            inspection_id: findInspectionServerIdWeb(String(data.inspection_id)),
          }
          const [result] = await APIServer.addQualityResults([payload])
          if (!result || !result.id) {
            throw new Error('결과 서버 생성에 실패했습니다.')
          }
          // 서버 id를 localStringId로 승격 → 새로고침 후에도 동일 식별자로 매칭
          applyWebPatch(data.localStringId, {
            id: result.id,
            localStringId: String(result.id),
            inspection_id: result.inspection_id,
            created_at: result.created_at,
            updated_at: result.updated_at,
            modifiedFromLocal: false,
          })
        } else {
          const [result] = await APIServer.updateQualityResults([
            {
              ...buildUploadPayload(data),
              id: Number(data.id),
            },
          ])
          applyWebPatch(data.localStringId, {
            updated_at: result?.updated_at ?? data.updated_at,
            modifiedFromLocal: false,
          })
        }
      } catch (error) {
        // 생성 실패 시 유령 항목이 남지 않도록 메모리에서 제거 후 호출자에 전파
        if (isNew && !isDeleted) {
          results.value = results.value.filter(item => item.localStringId !== data.localStringId)
        }
        throw error
      }
    }
  }

  async function onlySetSqlite(items: QualityInspectionResult[]): Promise<void> {
    const { itemsToProcess, columns } = await SQLite.getNeedUpdateItems(items, storeKey)
    await SQLite.updateSqliteOnly(itemsToProcess, columns, currentTaskDatabaseId.value, storeKey)
  }

  const addApiQueue = async (
    itemsToProcess: { item: QualityInspectionResult; itemId: string }[],
  ): Promise<void> => {
    if (itemsToProcess.length === 0) return
    for (const { item } of itemsToProcess) {
      // 로컬에서 변경된 항목만 동기화 대상으로 삼는다
      if (!item.modifiedFromLocal) continue
      await addResultJobToQueue(item)
    }
  }

  /**
   * 세션의 서버 id를 조회한다 (defect의 findShapeId와 동일 개념).
   * @param inspectionKey 세션의 localStringId 또는 id
   * @returns 서버 id / 'is_deleted' / '_need_to_upload'
   */
  async function findInspectionId(inspectionKey: string): Promise<number | string> {
    const db = await SQL.getDatabase()
    const stripped = inspectionKey.replace('_need_to_upload', '')
    const result = await db.query(
      'SELECT id, deleted_at FROM quality_inspections WHERE localStringId = ? OR id = ? OR localStringId = ? OR id = ?',
      [inspectionKey, inspectionKey, stripped, stripped],
    )
    const row = result.values?.[0]
    if (!row) return '_need_to_upload'
    if (row.deleted_at) return 'is_deleted'
    return row.id
  }

  /**
   * 결과 동기화 작업을 SyncQueue에 추가
   * @param item 동기화할 결과 데이터
   */
  async function addResultJobToQueue(item: QualityInspectionResult): Promise<void> {
    const isNew = String(item.id).includes('_need_to_upload')
    // 자동 플래그(병합/404 처리)가 아닌 사용자 의도 삭제만 서버 DELETE로 전파
    const isDeleted = item.deleted_at && !!item.fields_updated_at?.deleted_at

    // 동일 항목의 기존 job 처리(PENDING 갱신·PROCESSING 대기열)는 syncQueue.addJob이 담당
    // - 여기서 스킵하면 PROCESSING 완료 시점의 mfl=false 덮어쓰기로 새 편집이 유실됨

    let executeCallback: (data: QualityInspectionResult) => Promise<QualityInspectionResult | void>

    if (isDeleted) {
      // 서버에 생성된 적 없고 진행 중인 업로드 job도 없으면 서버에 알릴 필요 없음
      if (isNew && !hasInFlightJob(item.localStringId)) {
        await individualSetLocalModified(item.localStringId, [
          { key: 'modifiedFromLocal', value: false },
        ])
        return
      }
      executeCallback = async (data: QualityInspectionResult): Promise<QualityInspectionResult> => {
        // 실행 시점 기준 서버 미생성 항목(업로드가 삭제로 무효화된 경우): 서버 호출 없이 종료
        if (String(data.id).includes('_need_to_upload')) {
          await individualSetLocalModified(data.localStringId, [
            { key: 'modifiedFromLocal', value: false },
          ])
          return data
        }
        await APIServer.deleteQualityResults([{ id: Number(data.id) }])
        await individualSetLocalModified(data.localStringId, [
          { key: 'modifiedFromLocal', value: false },
        ])
        return data
      }
    } else if (isNew) {
      executeCallback = async (data: QualityInspectionResult): Promise<QualityInspectionResult> => {
        // 업로드 전에 로컬에서 삭제된 항목: 서버에 생성하지 않고 동기화 종료
        if (data.deleted_at) {
          await individualSetLocalModified(data.localStringId, [
            { key: 'modifiedFromLocal', value: false },
          ])
          return data
        }

        // 의존성 처리: 세션이 로컬-only(서버 id 없음)이면 업로드 대기
        const inspectionId = await findInspectionId(String(data.inspection_id))
        if (inspectionId === 'is_deleted') {
          // 세션이 삭제됨 → 결과도 로컬 삭제 처리
          const deleteTime = Merge.getCurrentTime()
          await individualSetLocalModified(data.localStringId, [
            { key: 'deleted_at', value: deleteTime },
            { key: 'modifiedFromLocal', value: false },
          ])
          return { ...data, deleted_at: deleteTime, modifiedFromLocal: false }
        }
        if (String(inspectionId).includes('_need_to_upload') || isNaN(Number(inspectionId))) {
          throw new DependencyNotReadyError(
            `Dependent inspection ${data.inspection_id} is not synced yet.`,
          )
        }

        const payload = {
          ...buildUploadPayload(data),
          inspection_id: Number(inspectionId),
        }
        const [result] = await APIServer.addQualityResults([payload])
        if (!result || !result.id) {
          throw new Error('Failed to create quality result on server.')
        }
        return result
      }
    } else {
      executeCallback = async (data: QualityInspectionResult): Promise<QualityInspectionResult> => {
        const [result] = await APIServer.updateQualityResults([
          {
            ...buildUploadPayload(data),
            id: Number(data.id),
          },
        ])
        return result
      }
    }

    syncQueue.addJob({
      localStringId: item.localStringId,
      data: item,
      priority: JobPriority.NORMAL,
      maxRetries: 5,
      tag: 'qualityResult',
      execute: executeCallback,
      onSuccess: async (_result, data) => {
        if (!isNew) {
          await individualSetLocalModified(data.localStringId, [
            { key: 'modifiedFromLocal', value: false },
          ])
        }
      },
      onError: async (error, data, errorCode) => {
        // 404(서버 삭제)/409(중복): 로컬 삭제 처리 후 큐에서 제거
        if (errorCode === 404 || errorCode === 409) {
          await individualSetLocalModified(data.localStringId, [
            { key: 'deleted_at', value: Merge.getCurrentTime() },
            { key: 'modifiedFromLocal', value: false },
          ])
          syncQueue.removeJobByLocalStringId(data.localStringId)
          return
        }
        // 403(권한: 타인 행 수정, 검수완료 잠금): 로컬 변경을 포기하고 큐에서 제거
        // 이후 병합(initData/전역 동기화)이 서버 상태로 되돌린다
        if (errorCode === 403) {
          console.warn(`[QualityResult] 권한 거부(403) - 로컬 변경 폐기: ${data.localStringId}`)
          if (isNew) {
            // 생성 자체가 거부됨 → 로컬-only 행을 삭제 처리 (유령 행·의존 job 무한 대기 방지)
            await individualSetLocalModified(data.localStringId, [
              { key: 'deleted_at', value: Merge.getCurrentTime() },
              { key: 'modifiedFromLocal', value: false },
            ])
          } else {
            // 수정 거부 → 필드 스탬프를 폐기해 다음 병합이 서버 상태로 복원하게 함
            await individualSetLocalModified(data.localStringId, [
              { key: 'modifiedFromLocal', value: false },
              { key: 'fields_updated_at', value: JSON.stringify({}) },
            ])
          }
          syncQueue.removeJobByLocalStringId(data.localStringId)
          return
        }
        console.error(`[SyncQueue] QualityResult job for ${data.localStringId} FAILED.`, error)
      },
      onIdMap: isNew
        ? async (localStringId: string, serverResult: QualityInspectionResult): Promise<void> => {
            if (!serverResult || !serverResult.id) {
              throw new Error('Invalid serverResult: missing id or data')
            }
            const updateKeys: { key: string; value: unknown }[] = [
              { key: 'id', value: serverResult.id },
              { key: 'inspection_id', value: serverResult.inspection_id },
              { key: 'created_at', value: serverResult.created_at },
              { key: 'updated_at', value: serverResult.updated_at },
            ]
            // 업로드 도중 로컬 삭제가 끼어든 경우 mfl을 유지해 삭제 job이 이어서 전파
            if (!(await isRowDeletedLocally(storeKey, localStringId))) {
              updateKeys.push({ key: 'modifiedFromLocal', value: false })
            }
            await individualSetLocalModified(localStringId, updateKeys)

            const resultInStore = results.value.find(r => r.localStringId === localStringId)
            if (resultInStore) {
              resultInStore.id = serverResult.id
              resultInStore.inspection_id = serverResult.inspection_id
              resultInStore.created_at = serverResult.created_at
              resultInStore.updated_at = serverResult.updated_at
              resultInStore.modifiedFromLocal = false
            }

            // 결과 id 확정 → 대기 중이던 사진 job 즉시 재시도
            syncQueue.wakeFailedJobs('qualityResultPhoto')
          }
        : undefined,
    })
  }

  /**
   * 결과 생성 (로컬 우선 저장 후 동기화 큐 등록)
   * @param data 생성할 결과 필드 (inspection_id·item_id 필수, inspection_id는 세션 localStringId 가능)
   * @returns 생성된 결과
   */
  async function Add(data: Partial<QualityInspectionResult>): Promise<QualityInspectionResult> {
    const localId = getRandomString(12, false)
    const currentTime = Merge.getCurrentTime()

    const result: QualityInspectionResult = {
      id: `${localId}_need_to_upload`,
      inspection_id: data.inspection_id ?? '',
      item_id: data.item_id ?? 0,
      task_id: Number(currentTaskDatabaseId.value),
      // 로컬 생성 행도 전역 동기화의 계정 필터에 매칭되도록 실제 company_id 부여
      company_id: useAuthStore().companyId ?? 0,
      created_user_id: 0,
      location: data.location ?? '',
      location_detail: data.location_detail ?? null,
      result: data.result ?? 'NA',
      note: data.note ?? '',
      note_2: data.note_2 ?? '',
      note_3: data.note_3 ?? '',
      set_count: data.set_count ?? 1,
      primary_set: data.primary_set ?? 1,
      custom_defect_content: data.custom_defect_content ?? '',
      floorplan_id: data.floorplan_id ?? null,
      shape_type: data.shape_type ?? '',
      shape_data: data.shape_data ?? null,
      balloon_number: data.balloon_number ?? null,
      sort_key: data.sort_key ?? getLastSortKey(getResults.value),
      fields_updated_at: {},
      created_at: currentTime,
      updated_at: currentTime,
      deleted_at: null,
      localStringId: localId,
      modifiedFromLocal: true,
    }

    // 생성 시점 값이 있는 필드 스탬프 (병합 시 로컬 값 보존용)
    const initialFieldsUpdatedAt: FieldsUpdatedAt = { inspection_id: currentTime }
    for (const key of UPLOAD_KEYS) {
      if (key === 'fields_updated_at') continue
      initialFieldsUpdatedAt[key] = currentTime
    }
    result.fields_updated_at = initialFieldsUpdatedAt

    if (!isApp()) {
      results.value.push(result)
    }
    await requestUpdate([result])
    return result
  }

  /**
   * 결과 수정
   * @param localStringId 대상 결과의 localStringId
   * @param changes 변경 필드
   */
  async function UpdateValue(
    localStringId: string,
    changes: Partial<QualityInspectionResult>,
  ): Promise<void> {
    const current = results.value.find(item => item.localStringId === localStringId)
    if (!current) return

    const changed: QualityInspectionResult = {
      ...current,
      ...changes,
      fields_updated_at: { ...current.fields_updated_at },
    }
    changed.fields_updated_at = Merge.findChangedField(current, changed)
    changed.modifiedFromLocal = true

    if (!isApp()) {
      const index = results.value.findIndex(item => item.localStringId === localStringId)
      if (index !== -1) results.value[index] = changed
    }
    await requestUpdate([changed])
  }

  /**
   * 결과 삭제 (soft-delete 후 서버 전파, 미업로드 사진은 사진 스토어가 함께 정리)
   * @param localStringId 대상 결과의 localStringId
   */
  async function Delete(localStringId: string): Promise<void> {
    const current = results.value.find(item => item.localStringId === localStringId)
    if (!current) return

    const target: QualityInspectionResult = {
      ...current,
      fields_updated_at: { ...current.fields_updated_at },
    }
    target.deleted_at = Merge.getCurrentTime()
    const fieldsUpdatedAt = Merge.setFieldUpdate(target.fields_updated_at, 'deleted_at')
    fieldsUpdatedAt.need_restore = false
    target.fields_updated_at = fieldsUpdatedAt
    target.modifiedFromLocal = true

    if (!isApp()) {
      const index = results.value.findIndex(item => item.localStringId === localStringId)
      if (index !== -1) results.value[index] = target
    }
    await requestUpdate([target])
  }

  async function getAllModifiedData(): Promise<QualityInspectionResult[]> {
    const db = await SQL.getDatabase()
    if (!SQL.createdTables.has(storeKey)) return []
    const localData = await db.query(`SELECT * FROM ${storeKey} WHERE modifiedFromLocal = 1`)
    return (localData.values ?? []).map((row: unknown) =>
      parseRow(row),
    ) as QualityInspectionResult[]
  }

  function allClear(): void {
    if (currentListener) {
      SQLiteEventBus.getInstance().offTableUpdated(storeKey, currentListener)
      currentListener = null
    }
    results.value = []
    currentTaskDatabaseId.value = ''
  }

  return {
    results,
    notUploadedResults,
    initData,
    getByTaskId,
    getResults,
    getResultsByInspection,
    GetItem,
    Add,
    UpdateValue,
    Delete,
    requestUpdate,
    uploadNotUploaded,
    addApiQueue,
    onlySetSqlite,
    getAllModifiedData,
    individualSetLocalModified,
    findInspectionId,
    allClear,
  }
})
